import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import toast from "react-hot-toast";
import clsx from "clsx";

const CHALLENGE_TYPES = {
  xp_race:     { label: "XP-Rennen",      icon: "⚡", unit: "XP" },
  quiz_count:  { label: "Quiz-Marathon",  icon: "📝", unit: "Quizze" },
  streak_race: { label: "Streak-Jagd",    icon: "🔥", unit: "Tage" },
  coins_race:  { label: "Coin-Jagd",      icon: "🪙", unit: "Coins" },
};

function timeLeft(endsAt) {
  const diff = new Date(endsAt) - Date.now();
  if (diff <= 0) return "Beendet";
  const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}T ${hours}h`;
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m`;
}

/* ── Leaderboard row ──────────────────────────────────────────────────────── */
function LeaderRow({ entry, targetValue }) {
  const pct = Math.min((entry.progress / targetValue) * 100, 100);
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-6 text-center text-sm">{medals[entry.rank - 1] || <span className="text-xs text-gray-500">#{entry.rank}</span>}</span>
      <span className="text-xl">{entry.avatar}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-white truncate">{entry.name}</p>
        <div className="h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: entry.completed ? "#22c55e" : "#8b5cf6" }}
          />
        </div>
      </div>
      <span className="text-xs font-bold text-gray-400 flex-shrink-0">{entry.progress}</span>
    </div>
  );
}

/* ── Challenge card ───────────────────────────────────────────────────────── */
function ChallengeCard({ challenge, onJoin, joining }) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = CHALLENGE_TYPES[challenge.type] || { label: challenge.type, icon: "🏆", unit: "" };
  const myProgress = challenge.myProgress?.progress || 0;
  const pct = Math.min((myProgress / challenge.targetValue) * 100, 100);
  const expired = challenge.isExpired;
  const myRank = challenge.leaderboard?.findIndex((e) => e.userId === challenge.myProgress?.userId);

  return (
    <div className={clsx(
      "border rounded-2xl overflow-hidden transition-all duration-200",
      expired ? "border-white/10 opacity-60" : "border-white/10 hover:border-violet-500/40"
    )}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer bg-white/5 hover:bg-white/8 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center text-2xl flex-shrink-0">
            {challenge.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold text-white leading-tight">{challenge.title}</p>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {expired
                  ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 font-bold">Beendet</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 font-bold">{timeLeft(challenge.endsAt)}</span>
                }
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-gray-500">
                  {typeInfo.icon} {typeInfo.label}
                </span>
              </div>
            </div>
            {challenge.description && (
              <p className="text-xs text-gray-400 mt-1 line-clamp-1">{challenge.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-[10px] text-gray-500">Ziel: <span className="text-white font-bold">{challenge.targetValue} {typeInfo.unit}</span></span>
              <span className="text-[10px] text-gray-500">👥 {challenge.participantCount}</span>
              <span className="text-[10px] text-yellow-400">+{challenge.rewardXP} XP · +{challenge.rewardCoins} 🪙</span>
            </div>
          </div>
        </div>

        {/* My progress bar */}
        {challenge.isJoined && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-gray-500">Mein Fortschritt</span>
              <span className="text-[10px] font-bold text-violet-300">{myProgress}/{challenge.targetValue}</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: pct >= 100 ? "#22c55e" : "linear-gradient(90deg, #7c3aed, #8b5cf6)" }}
              />
            </div>
            {myRank >= 0 && (
              <p className="text-[10px] text-gray-500 mt-1">Platz #{myRank + 1} im Ranking</p>
            )}
          </div>
        )}
      </div>

      {/* Join button */}
      {!challenge.isJoined && !expired && (
        <div className="px-4 pb-4 bg-white/5">
          <Button
            full
            size="sm"
            loading={joining}
            onClick={(e) => { e.stopPropagation(); onJoin(challenge.id); }}
          >
            🏁 Mitmachen
          </Button>
        </div>
      )}

      {/* Leaderboard expansion */}
      {expanded && challenge.leaderboard?.length > 0 && (
        <div className="px-4 pb-4 bg-black/20 border-t border-white/5">
          <p className="text-[10px] font-bold text-gray-500 mt-3 mb-2 uppercase tracking-wider">Top Teilnehmer</p>
          <div className="divide-y divide-white/5">
            {challenge.leaderboard.slice(0, 5).map((e) => (
              <LeaderRow key={e.userId} entry={e} targetValue={challenge.targetValue} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
function Skeleton({ className }) {
  return <div className={clsx("animate-pulse rounded-xl bg-white/5", className)} />;
}

/* ── Main ─────────────────────────────────────────────────────────────────── */
export default function ClassChallenges() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["challenges"],
    queryFn: () => api.get("/challenges").then((r) => r.data),
    staleTime: 30_000,
  });

  const join = useMutation({
    mutationFn: (id) => api.post(`/challenges/${id}/join`),
    onSuccess: () => {
      toast.success("Du nimmst jetzt teil! 🏁");
      qc.invalidateQueries({ queryKey: ["challenges"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Skeleton className="h-8 w-52" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 w-full" />)}
      </div>
    );
  }

  const challenges = data?.challenges || [];
  const active  = challenges.filter((c) => !c.isExpired);
  const expired = challenges.filter((c) =>  c.isExpired);

  return (
    <div className="space-y-5 animate-fade-in max-w-2xl mx-auto">
      <div>
        <h1 className="text-xl font-black flex items-center gap-2">
          <span className="text-2xl">🏆</span>
          <span>Klassen-Challenges</span>
        </h1>
        <p className="text-xs text-gray-400 mt-0.5">Tritt gegen deine Klasse an und verdiene Belohnungen</p>
      </div>

      {active.length === 0 && expired.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-20 h-20 rounded-2xl bg-violet-500/20 flex items-center justify-center text-4xl">🏆</div>
          <div className="text-center">
            <p className="font-bold text-gray-300">Keine aktiven Challenges</p>
            <p className="text-xs text-gray-500 mt-1">Dein Lehrer hat noch keine Challenge erstellt</p>
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-3">
          {active.map((c) => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              onJoin={(id) => join.mutate(id)}
              joining={join.isPending}
            />
          ))}
        </div>
      )}

      {expired.length > 0 && (
        <div>
          <h2 className="text-xs font-bold text-gray-600 mb-2 uppercase tracking-wider">Abgeschlossen</h2>
          <div className="space-y-2">
            {expired.slice(0, 3).map((c) => (
              <ChallengeCard key={c.id} challenge={c} onJoin={() => {}} joining={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
